import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, DollarSign, Clock, Share2, X, Send, Search, Camera, Upload, Facebook, Twitter, Linkedin, Link as LinkIcon, Users, Compass, Volume2, VolumeX, Bookmark } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../context/AuthContext';
import { grossFromNet, fmtMoney } from '../lib/fees';
import './Feed.css';

// Stream Cloudinary videos as H.264 MP4 (plays on every browser, starts fast).
const playableUrl = (u) => {
  if (!/res\.cloudinary\.com\/.+\/video\/upload\//.test(u)) return u;
  if (/\/video\/upload\/(c_|q_|vc_|f_|h_|w_)/.test(u)) return u;
  return u.replace('/video/upload/', '/video/upload/c_limit,h_1280,q_auto,vc_h264/')
    .replace(/\.(mov|m4v|webm|mkv|avi|3gp|hevc)$/i, '.mp4');
};

const Feed = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [liked, setLiked] = useState({});
  const [showAlarmDialog, setShowAlarmDialog] = useState(false);
  const [showRatesDialog, setShowRatesDialog] = useState(false);
  const [showCommentDialog, setShowCommentDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState([]);
  const [activeTab, setActiveTab] = useState('foryou');
  const [alarmMinutes, setAlarmMinutes] = useState(null);
  const [alarmActive, setAlarmActive] = useState(false);
  const [alarmTimeLeft, setAlarmTimeLeft] = useState(0);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDesc, setUploadDesc] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const timerRef = useRef(null);
  const [savedIds, setSavedIds] = useState({}); // { [videoId]: true }
  const [showSoundPrompt, setShowSoundPrompt] = useState(true);

  // Fetch videos
  // NOTE: never inject fake sample users like "David / Sarah Johnson / Mike Chen"
  // as a fallback. Empty === empty. Especially for the Following tab, hard-coded
  // names look real and mislead users into thinking those are people they follow.
  // The empty state below the feed tells the user exactly what to do next.
  const PAGE = 10;
  const [hasMore, setHasMore] = useState(true);
  const loadingMoreRef = useRef(false);
  const fetchVideos = useCallback(async () => {
    try {
      const data = await api(`/api/videos/feed?feed_type=${activeTab}&user_id=${user?.id || ''}&limit=${PAGE}&skip=0`);
      const list = (data && data.videos) || [];
      setVideos(list);
      setHasMore(list.length === PAGE);
      const likedMap = {};
      list.forEach(v => {
        if (v.likes && v.likes.includes(user?.id)) likedMap[v.id] = true;
      });
      setLiked(likedMap);
    } catch {
      setVideos([]);
    }
  }, [activeTab, user?.id]);

  useEffect(() => {
    setCurrentIndex(0);
    if (feedRef.current) feedRef.current.scrollTop = 0;
    fetchVideos();
  }, [fetchVideos]);

  // Load the next page when the viewer is 3 clips from the end.
  useEffect(() => {
    if (!hasMore || loadingMoreRef.current || videos.length === 0 || currentIndex < videos.length - 3) return;
    loadingMoreRef.current = true;
    api(`/api/videos/feed?feed_type=${activeTab}&user_id=${user?.id || ''}&limit=${PAGE}&skip=${videos.length}`)
      .then((data) => {
        const more = (data && data.videos) || [];
        setHasMore(more.length === PAGE);
        setVideos(prev => {
          const seen = new Set(prev.map(v => v.id));
          return [...prev, ...more.filter(v => !seen.has(v.id))];
        });
        setLiked(prev => {
          const m = { ...prev };
          more.forEach(v => { if (v.likes && v.likes.includes(user?.id)) m[v.id] = true; });
          return m;
        });
      })
      .catch(() => {})
      .finally(() => { loadingMoreRef.current = false; });
  }, [currentIndex, videos.length, hasMore, activeTab, user?.id]);

  // Hydrate saved-video state from the current user object
  useEffect(() => {
    if (user?.saved_videos) {
      const map = {};
      user.saved_videos.forEach(id => { map[id] = true; });
      setSavedIds(map);
    }
  }, [user?.saved_videos]);

  const [muted, setMuted] = useState(true);
  const mutedRef = useRef(true);
  const curRef = useRef(0);
  useEffect(() => { mutedRef.current = muted; }, [muted]);
  useEffect(() => { curRef.current = currentIndex; }, [currentIndex]);

  // 5 reusable <video> slots cover clips current-2..current+2 (iOS limits live decoders,
  // and reused elements keep their "sound allowed" unlock after the first tap).
  const SLOTS = 5;
  const slotIndexes = Array.from({ length: SLOTS }, (_, s) => {
    for (let i = currentIndex - 2; i <= currentIndex + 2; i++) {
      if (i >= 0 && i < videos.length && i % SLOTS === s) return i;
    }
    return -1;
  });

  // Play ONLY the on-screen clip; every other clip is paused, muted and rewound.
  // If the browser refuses audible playback, fall back to muted so the clip still plays.
  const playOnly = useCallback((idx) => {
    curRef.current = idx;
    document.querySelectorAll('.feed-container video[data-index]').forEach((v) => {
      const i = Number(v.dataset.index);
      if (i === idx) {
        v.muted = mutedRef.current;
        const p = v.play();
        if (p && p.catch) p.catch((err) => {
          if (err?.name !== 'NotAllowedError' || Number(v.dataset.index) !== curRef.current) return;
          v.muted = true;
          v.play().catch(() => {});
          if (!mutedRef.current) { mutedRef.current = true; setMuted(true); setShowSoundPrompt(true); }
        });
      } else {
        v.muted = true;
        if (!v.paused) v.pause();
        if (v.currentTime > 0) v.currentTime = 0;
      }
    });
  }, []);

  useEffect(() => { playOnly(currentIndex); }, [currentIndex, videos, muted, playOnly]);

  // Called from a tap (user gesture): unlock every slot for audible playback so
  // later swipes can start the next clip with sound on iOS.
  const unlockAll = () => {
    document.querySelectorAll('.feed-container video.feed-slot').forEach((v) => {
      if (Number(v.dataset.index) === curRef.current || !v.getAttribute('src')) return;
      v.muted = false;
      const p = v.play();
      if (p && p.then) p.then(() => {
        if (Number(v.dataset.index) !== curRef.current) { v.pause(); v.muted = true; v.currentTime = 0; }
      }).catch(() => { v.muted = true; });
    });
  };

  const toggleMute = () => {
    const next = !muted;
    mutedRef.current = next;
    if (!next) unlockAll();
    setMuted(next);
    setShowSoundPrompt(false);
  };

  const dismissSoundPrompt = () => {
    mutedRef.current = false;
    unlockAll();
    setMuted(false);
    setShowSoundPrompt(false);
  };

  // Toggle save/unsave the current video
  const toggleSave = async () => {
    if (!currentVideo || currentVideo.id.startsWith('sample')) return;
    const wasSaved = !!savedIds[currentVideo.id];
    setSavedIds(prev => ({ ...prev, [currentVideo.id]: !wasSaved }));
    try {
      await api(`/api/videos/${currentVideo.id}/save`, { method: 'POST' });
      refreshUser?.();
    } catch {
      setSavedIds(prev => ({ ...prev, [currentVideo.id]: wasSaved })); // revert
    }
  };

  const currentVideo = videos[currentIndex];
  const [ratePkgs, setRatePkgs] = useState(null);
  useEffect(() => {
    if (!showRatesDialog || !currentVideo?.user_id) return;
    setRatePkgs(null);
    api(`/api/users/${currentVideo.user_id}`)
      .then(d => setRatePkgs(d?.user?.booking_packages || []))
      .catch(() => setRatePkgs([]));
  }, [showRatesDialog, currentVideo?.user_id]);

  // Whichever card is mostly on screen becomes the active (playing, audible) one.
  const feedRef = useRef(null);
  useEffect(() => {
    const root = feedRef.current;
    if (!root) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) {
          const i = Number(en.target.dataset.index);
          playOnly(i);
          setCurrentIndex(i);
        }
      });
    }, { root, threshold: 0.6 });
    root.querySelectorAll('.video-item[data-index]').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [videos, playOnly]);

  // Desktop: one clip per wheel gesture / arrow key, like TikTok web.
  useEffect(() => {
    const root = feedRef.current;
    if (!root) return;
    let lock = 0;
    const go = (dir) => {
      const next = Math.max(0, Math.min(videos.length - 1, curRef.current + dir));
      const el = root.querySelector(`.video-item[data-index="${next}"]`);
      if (el) root.scrollTo({ top: el.offsetTop, behavior: 'smooth' });
    };
    const onWheel = (e) => {
      if (Math.abs(e.deltaY) < 8) return;
      e.preventDefault();
      const now = Date.now();
      if (now - lock < 650) return;
      lock = now;
      go(e.deltaY > 0 ? 1 : -1);
    };
    const onKey = (e) => {
      const tag = (document.activeElement?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || document.querySelector('.dialog-overlay')) return;
      if (e.key === 'ArrowDown' || e.key === 'PageDown') { e.preventDefault(); go(1); }
      else if (e.key === 'ArrowUp' || e.key === 'PageUp') { e.preventDefault(); go(-1); }
    };
    root.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('keydown', onKey);
    return () => { root.removeEventListener('wheel', onWheel); window.removeEventListener('keydown', onKey); };
  }, [videos.length]);

  // Like
  const toggleLike = async () => {
    if (!currentVideo || currentVideo.id.startsWith('sample')) return;
    try {
      const data = await api('/api/like', { method: 'POST', body: JSON.stringify({ user_id: user.id, video_id: currentVideo.id }) });
      setLiked(prev => ({ ...prev, [currentVideo.id]: data.liked }));
      setVideos(prev => prev.map(v => v.id === currentVideo.id ? { ...v, like_count: data.like_count } : v));
    } catch { /* fallback */ setLiked(prev => ({ ...prev, [currentVideo.id]: !prev[currentVideo.id] })); }
  };

  // Comments
  const openComments = async () => {
    setShowCommentDialog(true);
    if (currentVideo && !currentVideo.id.startsWith('sample')) {
      try {
        const data = await api(`/api/comments/${currentVideo.id}`);
        setComments(data.comments || []);
      } catch { setComments([]); }
    }
  };

  const handleSendComment = async () => {
    if (!commentText.trim() || !currentVideo || currentVideo.id.startsWith('sample')) return;
    try {
      const data = await api('/api/comments', {
        method: 'POST',
        body: JSON.stringify({ video_id: currentVideo.id, user_id: user.id, username: user.full_name, text: commentText })
      });
      setComments(prev => [data.comment, ...prev]);
      setCommentText('');
      setVideos(prev => prev.map(v => v.id === currentVideo.id ? { ...v, comment_count: (v.comment_count || 0) + 1 } : v));
    } catch { /* ignore */ }
  };

  // Alarm timer
  const startAlarm = (minutes) => {
    setAlarmMinutes(minutes);
    setAlarmTimeLeft(minutes * 60);
    setAlarmActive(true);
    setShowAlarmDialog(false);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setAlarmTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setAlarmActive(false);
          alert(`Your ${minutes}-minute timer is up! Time to take a break.`);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  // Share
  const handleShare = (platform) => {
    const url = window.location.origin + (currentVideo ? `/video/${currentVideo.id}` : '');
    const text = `Check out this video on 6th Mate!`;
    const shareUrls = {
      Facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      Twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
      LinkedIn: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      WhatsApp: `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`,
      'Copy Link': null
    };
    if (platform === 'Copy Link') {
      navigator.clipboard.writeText(url).then(() => alert('Link copied!'));
    } else if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank');
    }
    setShowShareDialog(false);
  };

  // Upload
  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('video', uploadFile);
      formData.append('description', uploadDesc);
      formData.append('tags', JSON.stringify([]));
      await api('/api/videos/upload', { method: 'POST', body: formData });
      setShowUploadDialog(false);
      setUploadFile(null);
      setUploadDesc('');
      fetchVideos();
    } catch (err) {
      alert('Upload failed: ' + err.message);
    }
    setUploading(false);
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  return (
    <div className="feed-container" ref={feedRef} data-testid="feed-container">
      {/* First-tap sound prompt — appears centered until the user taps to unmute
          or dismiss. After first interaction, the small mute toggle in the
          action rail takes over. */}
      {showSoundPrompt && videos.length > 0 && muted && (
        <button
          type="button"
          onClick={dismissSoundPrompt}
          data-testid="sound-prompt"
          aria-label="Tap for sound"
          style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            zIndex: 90, background: 'rgba(0,0,0,0.72)', border: '1px solid rgba(255,255,255,0.25)',
            color: '#fff', borderRadius: 999, padding: '18px 26px',
            display: 'inline-flex', alignItems: 'center', gap: 12,
            fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 15,
            backdropFilter: 'blur(8px)', cursor: 'pointer',
            boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
            animation: 'soundPromptPulse 2.2s ease-in-out infinite',
          }}
        >
          <VolumeX size={26} /> Tap for sound
        </button>
      )}
      <style>{`
        @keyframes soundPromptPulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); box-shadow: 0 12px 40px rgba(0,0,0,0.5); }
          50%      { transform: translate(-50%, -50%) scale(1.06); box-shadow: 0 16px 52px rgba(224,122,95,0.45); }
        }
      `}</style>
      {/* Timer badge */}
      {alarmActive && (
        <div style={{ position: 'fixed', top: 60, left: '50%', transform: 'translateX(-50%)', zIndex: 100, background: 'rgba(224,122,95,0.95)', color: '#fff', padding: '6px 16px', borderRadius: 20, fontSize: 14, fontWeight: 600 }} data-testid="alarm-badge">
          {formatTime(alarmTimeLeft)} remaining
        </div>
      )}

      {/* Top Navigation Bar */}
      <div className="feed-top-nav">
        <div className="feed-tabs">
          <button className={`feed-tab ${activeTab === 'following' ? 'active' : ''}`} onClick={() => setActiveTab('following')} data-testid="tab-following">Following</button>
          <button className={`feed-tab ${activeTab === 'foryou' ? 'active' : ''}`} onClick={() => setActiveTab('foryou')} data-testid="tab-foryou">For You</button>
        </div>
        <div className="feed-top-icons">
          <button className="feed-top-icon-btn" data-testid="search-btn"><Search size={24} /></button>
          <button className="feed-top-icon-btn" onClick={() => setShowUploadDialog(true)} data-testid="upload-btn"><Camera size={24} /></button>
        </div>
      </div>

      {videos.length === 0 && (
        <div className="feed-empty" data-testid={`feed-empty-${activeTab}`}
             style={{
               position: 'absolute', inset: 0, display: 'flex',
               flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
               padding: '0 32px', textAlign: 'center', color: '#fff', gap: 16,
               background: 'linear-gradient(160deg, #1a1a1a 0%, #2a1f1c 100%)',
             }}>
          {activeTab === 'following' ? (
            <>
              <Users size={56} strokeWidth={1.4} style={{ opacity: 0.7 }} />
              <h3 style={{ fontFamily: 'Fraunces, serif', fontSize: 22, fontWeight: 700, margin: 0 }}>
                No one to follow yet
              </h3>
              <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)', lineHeight: 1.5, maxWidth: 320, margin: 0 }}>
                Head to Discovery to find mentors, tap <strong style={{ color: '#E07A5F' }}>Follow</strong> on their profile, and their videos will show up here.
              </p>
              <button
                onClick={() => navigate('/discovery')}
                data-testid="feed-empty-cta-discovery"
                style={{
                  marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 8,
                  background: '#E07A5F', color: '#fff', border: 'none',
                  padding: '12px 20px', borderRadius: 999, fontWeight: 600, fontSize: 14,
                  cursor: 'pointer', boxShadow: '0 6px 20px rgba(224,122,95,0.35)',
                }}
              >
                <Compass size={16} /> Browse mentors
              </button>
            </>
          ) : (
            <>
              <Camera size={56} strokeWidth={1.4} style={{ opacity: 0.7 }} />
              <h3 style={{ fontFamily: 'Fraunces, serif', fontSize: 22, fontWeight: 700, margin: 0 }}>
                No videos yet
              </h3>
              <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)', lineHeight: 1.5, maxWidth: 320, margin: 0 }}>
                Be the first to share a story — or check back soon.
              </p>
              <button
                onClick={() => setShowUploadDialog(true)}
                data-testid="feed-empty-cta-upload"
                style={{
                  marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 8,
                  background: '#E07A5F', color: '#fff', border: 'none',
                  padding: '12px 20px', borderRadius: 999, fontWeight: 600, fontSize: 14,
                  cursor: 'pointer', boxShadow: '0 6px 20px rgba(224,122,95,0.35)',
                }}
              >
                <Camera size={16} /> Record a video
              </button>
            </>
          )}
        </div>
      )}

      {slotIndexes.map((idx, s) => {
        const v = idx >= 0 ? videos[idx] : null;
        const ok = !!(v && /^https?:\/\//.test(v.video_url || ''));
        return (
          <video
            key={`slot-${s}`}
            className="video-player feed-slot"
            data-index={ok ? idx : undefined}
            src={ok ? playableUrl(v.video_url) : undefined}
            poster={ok ? (v.thumbnail || undefined) : undefined}
            muted
            loop
            playsInline
            preload="auto"
            data-testid={ok ? `video-player-${v.id}` : `video-slot-${s}`}
            onCanPlay={(e) => { if (Number(e.currentTarget.dataset.index) === curRef.current) playOnly(curRef.current); }}
            style={{ top: `calc(${Math.max(idx, 0)} * (100vh - 70px))`, visibility: ok ? 'visible' : 'hidden' }}
          />
        );
      })}

      {videos.map((video, index) => (
        <div key={video.id} data-index={index} className={`video-item ${index === currentIndex ? 'active' : ''}`} data-testid={`video-item-${index}`}>
          {/* Real <video> element when the item has a Cloudinary/http URL.
              Falls back to the background thumbnail otherwise (samples/seeds).
              Auto-plays only for the active card; muted-autoplay is required
              on mobile Safari — a tap-to-unmute affordance sits below. */}
          {video.video_url && /^https?:\/\//.test(video.video_url) ? null : (
            <div className="video-background" style={{ backgroundImage: `url(${video.thumbnail || 'https://images.unsplash.com/photo-1590602847861-f357a9332bbc?w=500&h=800&fit=crop'})` }}>
              <div className="video-overlay"></div>
            </div>
          )}
          <div className="user-info">
            <img
              src={video.user_avatar || 'https://i.pravatar.cc/150?img=12'}
              alt={video.username}
              className="avatar"
              role="button"
              style={{ cursor: 'pointer' }}
              onClick={() => video.user_id && navigate(`/discovery/${video.user_id}`)}
              data-testid={`feed-avatar-${index}`}
            />
            <div className="user-details">
              <h3
                role="button"
                style={{ cursor: 'pointer' }}
                onClick={() => video.user_id && navigate(`/discovery/${video.user_id}`)}
                data-testid={`feed-username-${index}`}
              >{video.username}</h3>
              <p>{video.description}</p>
            </div>
          </div>
          <div className="actions">
            <button className={`action-btn ${liked[video.id] ? 'liked' : ''}`} onClick={toggleLike} data-testid="like-btn">
              <Heart size={32} fill={liked[video.id] ? '#ff1744' : 'none'} />
              <span>{video.like_count || 0}</span>
            </button>
            <button className="action-btn" onClick={openComments} data-testid="comment-btn">
              <MessageCircle size={32} />
              <span>{video.comment_count || 0}</span>
            </button>
            <button className={`action-btn ${savedIds[video.id] ? 'saved' : ''}`} onClick={toggleSave} data-testid="save-btn">
              <Bookmark size={32} fill={savedIds[video.id] ? '#E07A5F' : 'none'} />
              <span>{savedIds[video.id] ? 'Saved' : 'Save'}</span>
            </button>
            <button className="action-btn" onClick={() => setShowRatesDialog(true)} data-testid="rates-btn">
              <DollarSign size={32} /><span>Rates</span>
            </button>
            <button className="action-btn" onClick={() => setShowAlarmDialog(true)} data-testid="alarm-btn">
              <Clock size={32} /><span>Alarm</span>
            </button>
            <button className="action-btn" onClick={() => setShowShareDialog(true)} data-testid="share-btn">
              <Share2 size={32} /><span>Share</span>
            </button>
            <button className="action-btn" onClick={toggleMute} data-testid={`mute-btn-${index}`} aria-label={muted ? 'Unmute' : 'Mute'}>
              {muted ? <VolumeX size={32} /> : <Volume2 size={32} />}
              <span>{muted ? 'Sound' : 'Mute'}</span>
            </button>
          </div>
        </div>
      ))}

      {/* Alarm Dialog */}
      {showAlarmDialog && (
        <div className="dialog-overlay" onClick={() => setShowAlarmDialog(false)}>
          <div className="dialog" onClick={(e) => e.stopPropagation()} data-testid="alarm-dialog">
            <h2>Set Time Limit</h2>
            <p>Set a reminder for how long you want to spend on the app</p>
            <div className="time-options">
              {[15, 30, 60, 120].map(m => (
                <button key={m} className="time-btn" onClick={() => startAlarm(m)} data-testid={`alarm-${m}`}>
                  {m < 60 ? `${m} min` : `${m / 60} hour${m > 60 ? 's' : ''}`}
                </button>
              ))}
            </div>
            {alarmActive && <p style={{ marginTop: 12, color: '#E07A5F', fontWeight: 600 }}>Timer active: {formatTime(alarmTimeLeft)} left</p>}
            <button className="close-btn" onClick={() => setShowAlarmDialog(false)}>Close</button>
          </div>
        </div>
      )}

      {/* Rates Dialog */}
      {showRatesDialog && (
        <div className="dialog-overlay" onClick={() => setShowRatesDialog(false)}>
          <div className="dialog rates-dialog" onClick={(e) => e.stopPropagation()} data-testid="rates-dialog">
            <button className="dialog-close-btn" onClick={() => setShowRatesDialog(false)}><X size={24} /></button>
            <h2>Book a Session</h2>
            <p className="dialog-subtitle">Select a package to book with {currentVideo.username}</p>
            <div className="rates-grid">
              {ratePkgs === null && <p className="dialog-subtitle" data-testid="rates-loading">Loading rates…</p>}
              {ratePkgs?.length === 0 && <p className="dialog-subtitle" data-testid="rates-empty">This mentor hasn’t set up any packages yet.</p>}
              {(ratePkgs || []).map((pkg, i) => (
                <div key={pkg.id || i} className="rate-card" onClick={() => {
                  setShowRatesDialog(false);
                  navigate(`/discovery/${currentVideo.user_id}?book=${encodeURIComponent(pkg.id)}`);
                }} data-testid={`rate-card-${i}`}>
                  <div className="rate-duration">{pkg.name || `${pkg.minutes} minutes`}</div>
                  <div className="rate-price" data-testid={`rate-price-${i}`}>{fmtMoney(grossFromNet(pkg.price))}</div>
                  <div className="rate-per-min">{pkg.minutes} min</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Comment Dialog */}
      {showCommentDialog && (
        <div className="dialog-overlay" onClick={() => setShowCommentDialog(false)}>
          <div className="dialog message-dialog" onClick={(e) => e.stopPropagation()} data-testid="comment-dialog">
            <button className="dialog-close-btn" onClick={() => setShowCommentDialog(false)}><X size={24} /></button>
            <h2>Comments</h2>
            <div style={{ maxHeight: 300, overflowY: 'auto', marginBottom: 12 }}>
              {comments.length === 0 ? <p style={{ color: '#888', textAlign: 'center', padding: 20 }}>No comments yet</p> :
                comments.map(c => (
                  <div key={c.id} style={{ padding: '8px 0', borderBottom: '1px solid #222' }}>
                    <strong style={{ color: '#fff' }}>{c.username}</strong>
                    <p style={{ color: '#ccc', margin: '4px 0 0', fontSize: 14 }}>{c.text}</p>
                  </div>
                ))
              }
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <textarea
                className="message-textarea"
                placeholder="Write a comment..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                rows={4}
                data-testid="comment-input"
                style={{ flex: 1, minHeight: 90, fontSize: 15, padding: '12px 14px', lineHeight: 1.4 }}
              />
              <button
                className="primary-btn send-btn"
                onClick={handleSendComment}
                disabled={!commentText.trim()}
                data-testid="comment-send-btn"
                aria-label="Send comment"
                style={{ width: 44, height: 44, minWidth: 44, padding: 0, borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Dialog */}
      {showShareDialog && (
        <div className="dialog-overlay" onClick={() => setShowShareDialog(false)}>
          <div className="dialog share-dialog" onClick={(e) => e.stopPropagation()} data-testid="share-dialog">
            <button className="dialog-close-btn" onClick={() => setShowShareDialog(false)}><X size={24} /></button>
            <h2>Share Video</h2>
            <div className="share-options">
              {[{ name: 'Facebook', icon: <Facebook size={24} /> }, { name: 'Twitter', icon: <Twitter size={24} /> }, { name: 'WhatsApp', icon: <MessageCircle size={24} /> }, { name: 'LinkedIn', icon: <Linkedin size={24} /> }, { name: 'Copy Link', icon: <LinkIcon size={24} /> }].map(s => (
                <button key={s.name} className={`share-btn ${s.name.toLowerCase().replace(' ', '-')}`} onClick={() => handleShare(s.name)} data-testid={`share-${s.name.toLowerCase()}`}>
                  {s.icon}<span>{s.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Upload Dialog */}
      {showUploadDialog && (
        <div className="dialog-overlay" onClick={() => setShowUploadDialog(false)}>
          <div className="dialog" onClick={(e) => e.stopPropagation()} data-testid="upload-dialog">
            <button className="dialog-close-btn" onClick={() => setShowUploadDialog(false)}><X size={24} /></button>
            <h2>Upload Video</h2>
            <input type="file" ref={fileInputRef} accept="video/*" style={{ display: 'none' }} onChange={(e) => setUploadFile(e.target.files[0])} />
            <button className="time-btn" onClick={() => fileInputRef.current?.click()} style={{ width: '100%', marginBottom: 12 }}>
              <Upload size={20} style={{ marginRight: 8 }} /> {uploadFile ? uploadFile.name : 'Choose Video'}
            </button>
            <textarea className="message-textarea" placeholder="Description..." value={uploadDesc} onChange={(e) => setUploadDesc(e.target.value)} rows={3} style={{ marginBottom: 12 }} />
            <button className="primary-btn send-btn" onClick={handleUpload} disabled={!uploadFile || uploading} style={{ width: '100%' }}>
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Feed;
